// path: dashboard/src/pages/financeOfficer/FinanceDashboardPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import Sidebar from "../../components/MasterSideBar";
import TopBar from "../../components/TopbarNav";
import FinanceStatsCards from "../../components/financeOfficer/FinanceStatsCards";
import FinanceTransactionTable from "../../components/financeOfficer/FinanceTransactionTable";
import FinanceCharts from "../../components/financeOfficer/FinanceCharts";
import ProjectFinancialCard from "../../components/financeOfficer/ProjectFinancialCard";
import BankBalancesView from "../../components/financeOfficer/BankBalancesView"; 
import PremiumHeader from "../../components/common/PremiumHeader";
import PremiumCard from "../../components/common/PremiumCard";
import { 
  fetchFinanceDashboard, 
  fetchFinanceTransactions, 
  fetchPayrollAnalytics, 
  fetchProjectsList,
  fetchFinancialOverview 
} from "../../services/finance.service";
import { FiFilter, FiXCircle, FiCalendar } from "react-icons/fi";

export const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/" />;
};

export default function FinanceDashboardPage() {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [transactions, setTransactions] = useState([]); 
  const [financialOverview, setFinancialOverview] = useState(null); 
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [payrollAnalytics, setPayrollAnalytics] = useState(null);
  const [projectsList, setProjectsList] = useState([]); 

  // --- FILTER STATE ---
  const [filters, setFilters] = useState({
    type: "", 
    projectId: "", 
    budgetCode: "", 
    startDate: "",
    endDate: "",
    filterPeriod: "", 
  });

  const [loading, setLoading] = useState(true);
  const [loadingOverview, setLoadingOverview] = useState(true); 
  const [error, setError] = useState("");

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const getMonthName = (monthNum) => {
    const date = new Date();
    date.setMonth(monthNum - 1);
    return date.toLocaleString('default', { month: 'long' });
  };

  const getAvailablePeriods = (trans) => {
    const periods = new Set();
    trans.filter(t => t.category === 'Payroll' || t.type === 'Payroll').forEach(t => {
        if (!t.date) return;
        const d = new Date(t.date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        periods.add(`${y}-${m}`);
    });
    return Array.from(periods).sort().reverse();
  };

  const availablePeriods = useMemo(() => getAvailablePeriods(transactions), [transactions]);

  // --- DATA FETCHING LOGIC ---
  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {
      let apiParams = { ...filters };

      // Handle Budget Code Logic
      if (filters.projectId && !filters.budgetCode) {
        const selectedProject = projectsList.find(p => p._id === filters.projectId);
        if (selectedProject && selectedProject.budgetCode) {
          apiParams.budgetCode = selectedProject.budgetCode;
        }
      }

      // Smart Period Filtering
      if (filters.type === 'payroll' && filters.filterPeriod) {
        const [year, month] = filters.filterPeriod.split('-');
        const startOfMonth = new Date(year, month - 1, 1).toISOString().split('T')[0];
        const endOfMonth = new Date(year, month, 0).toISOString().split('T')[0]; 
        apiParams.startDate = startOfMonth;
        apiParams.endDate = endOfMonth;
      }

      const trans = await fetchFinanceTransactions(apiParams);
      setTransactions(trans.data || []);

      // Payroll Analytics
      if (!filters.type || filters.type === 'payroll') {
        try {
            const analytics = await fetchPayrollAnalytics(apiParams.budgetCode);
            setPayrollAnalytics(analytics);
        } catch(e) { console.error("Payroll analytics failed", e); }
      } else {
          setPayrollAnalytics(null);
      }

    } catch (err) {
      console.error(err);
      setError("Failed to load financial data.");
    } finally {
      setLoading(false);
    }
  };

  // --- UPDATED: Calculate Project Stats LOCALLY ---
  const currentProjectStats = useMemo(() => {
    if (!filters.projectId || transactions.length === 0) return null;

    const project = projectsList.find(p => p._id === filters.projectId);
    if (!project) return null;

    // 1. Calculate Income (Donations)
    const totalIncome = transactions
      .filter(t => t.type === 'INCOME')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

    // 2. Calculate Expenses (Total)
    const totalExpense = transactions
      .filter(t => t.type === 'EXPENSE')
      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

    // 3. Calculate Breakdown by Category (Now includes Items)
    const breakdown = {
      procurement: 0,
      travel: 0,
      assets: 0,
      payroll: 0,
      items: 0, // ADDED
    };

    transactions.forEach(t => {
      if (t.type === 'EXPENSE') {
        const amount = Number(t.amount) || 0;
        if (t.category === 'Procurement') breakdown.procurement += amount;
        if (t.category === 'Travel') breakdown.travel += amount;
        if (t.category === 'Asset Purchase') breakdown.assets += amount;
        if (t.category === 'Payroll') breakdown.payroll += amount;
        if (t.category === 'Item Consumption') breakdown.items += amount; // ADDED
      }
    });

    // 4. Get Target Budget from Project Settings
    const targetBudget = project.projectBudget || 0;
    
    // 5. CALCULATE REMAINING (CASH BALANCE)
    // Logic: You spend what you have actually received (Income), not just the plan (Budget).
    const cashBalance = totalIncome - totalExpense;
    
    // Budget remaining is still useful to know how close we are to the target
    const budgetRemaining = targetBudget - totalExpense;

    // Return object matching the shape expected by ProjectFinancialCard
    return {
      project: {
        id: project._id,
        title: project.projectTitle,
        status: project.status
      },
      budget: {
        total: targetBudget,     // The Project's Set Budget (Reference)
        allocated: totalIncome,  // Actual Donations Received
        remaining: cashBalance   // ACTUAL CASH LEFT (Income - Expense)
      },
      spend: {
        totalCommitted: totalExpense,
        breakdown: breakdown
      },
      metrics: {
        // Utilization based on Target Budget (Are we overspending the plan?)
        budgetUtilization: targetBudget > 0 ? ((totalExpense / targetBudget) * 100).toFixed(2) : 0,
        // Burn rate based on Income (Are we spending faster than we earn?)
        incomeBurnRate: totalIncome > 0 ? ((totalExpense / totalIncome) * 100).toFixed(2) : 0,
        plannedVsActual: {
            planned: targetBudget,
            committed: totalExpense,
            variance: targetBudget - totalExpense
        }
      }
    };
  }, [transactions, filters.projectId, projectsList]);


  const fetchOverviewData = async () => {
    setLoadingOverview(true);
    try {
      const data = await fetchFinancialOverview();
      setFinancialOverview(data);
    } catch (err) {
      console.error("Failed to fetch financial overview", err);
    } finally {
      setLoadingOverview(false);
    }
  };

  useEffect(() => {
    const getProjects = async () => {
      try {
        const list = await fetchProjectsList(); 
        setProjectsList(list);
      } catch (err) {
        console.error("Failed to load projects list");
      }
    };
    getProjects();
    fetchOverviewData(); 
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters]); 

  useEffect(() => {
    if (filters.type !== 'payroll' && filters.filterPeriod) {
      setFilters(prev => ({ ...prev, filterPeriod: "" }));
    }
  }, [filters.type]);

  const clearFilter = (key) => {
    if (key === 'projectId') {
        setFilters(prev => ({ ...prev, [key]: "", budgetCode: "" }));
    } else {
        setFilters(prev => ({ ...prev, [key]: "" }));
    }
  };

  const getActiveChartType = () => {
    if (filters.type === 'payroll') return 'payroll';
    return 'cashflow';
  };

  const datesDisabled = filters.type === 'payroll' && filters.filterPeriod !== "";

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans overflow-hidden">
      <Sidebar 
        onLogout={handleLogout} 
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />

      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'ml-0 md:ml-64'}`}>
        <TopBar onSidebarToggle={() => {}} />

        <div className="mt-20 md:mt-24 p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Finance Officer Dashboard
              </h1>
              <p className="text-sm text-gray-500">
                Budget code balancing and financial tracking
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-center justify-between">
              <span>{error}</span>
              <button onClick={() => setError("")} className="text-red-500 hover:text-red-700"><FiXCircle /></button>
            </div>
          )}

          {loadingOverview ? (
             <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
             </div>
          ) : (
            <BankBalancesView 
                overviewData={financialOverview} 
                projectsList={projectsList} 
                filters={filters}
                transactions={transactions}
            />
          )}

          {loading ? (
            <div className="flex justify-center items-center h-64 mt-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
          ) : (
            <div className="space-y-6 mt-6">
              
              {currentProjectStats && filters.type !== 'payroll' && (
                <ProjectFinancialCard data={currentProjectStats} />
              )}

              <FinanceStatsCards 
                transactions={transactions} 
                filterType={filters.type}
              />

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {filters.type === 'payroll' && payrollAnalytics ? (
                  <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500">
                    <h3 className="text-lg font-bold text-gray-700 mb-4">Payroll Expenditure Trends</h3>
                    <FinanceCharts type="payroll" data={payrollAnalytics.trends} />
                  </div>
                ) : null}

                <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
                  <h3 className="text-lg font-bold text-gray-700 mb-4">Cash Flow (Income vs Expense)</h3>
                  <FinanceCharts type="cashflow" data={transactions} />
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <h2 className="text-xl font-bold text-gray-800">Universal Transaction Ledger</h2>
                    
                    <div className="flex flex-wrap gap-3 items-center">
                      <select
                        className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                        value={filters.type}
                        onChange={(e) => {
                            setFilters({
                              ...filters,
                              type: e.target.value,
                            });
                          }}
                      >
                        <option value="">All Types</option>
                        <option value="donation">Donations (Income)</option>
                        <option value="payroll">Payroll (Expense)</option>
                        <option value="procurement">Procurement (Expense)</option>
                        <option value="travel">Travel (Expense)</option>
                        <option value="asset">Assets (Expense)</option>
                        {/* ADDED ITEM OPTION */}
                        <option value="item">Item Consumption (Expense)</option>
                      </select>

                      {filters.type === 'payroll' && (
                        <div className="relative">
                           <select
                              value={filters.filterPeriod}
                              onChange={(e) => setFilters({ ...filters, filterPeriod: e.target.value })}
                              className="appearance-none bg-white border border-orange-300 text-orange-700 py-1.5 pl-3 pr-8 rounded text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 font-medium"
                            >
                              <option value="">Select Period...</option>
                              {availablePeriods.map(period => {
                                const [y, m] = period.split('-');
                                return (
                                  <option key={period} value={period}>
                                    {getMonthName(parseInt(m))} {y}
                                  </option>
                                )
                              })}
                            </select>
                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-orange-700">
                              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                            </div>
                        </div>
                      )}

                      <div className="relative">
                        <select
                          className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none min-w-[250px]"
                          value={filters.projectId}
                          onChange={(e) => setFilters({ ...filters, projectId: e.target.value, budgetCode: "" })}
                        >
                          <option value="">Filter by Budget Code / Project...</option>
                          {projectsList.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.budgetCode ? `(${p.budgetCode}) ` : ""}{p.projectTitle}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={`flex items-center gap-2 ${datesDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
                        <input
                          type="date"
                          className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                          value={filters.startDate}
                          onChange={(e) => setFilters({ ...filters, startDate: e.target.value, filterPeriod: "" })} 
                        />
                        <span className="text-gray-500 mx-1">to</span>
                        <input
                          type="date"
                          className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                          value={filters.endDate}
                          onChange={(e) => setFilters({ ...filters, endDate: e.target.value, filterPeriod: "" })} 
                        />
                        {(filters.startDate || filters.endDate) && (
                            <button 
                                onClick={() => { setFilters({...filters, startDate: '', endDate: ''}) }}
                                className="text-xs text-red-500 hover:text-red-700 underline"
                            >
                                <FiXCircle className="w-3 h-3 inline"/>
                            </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <FinanceTransactionTable transactions={transactions} refresh={fetchData} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}